import { apiClient } from '@devd-client/api';
import { getToken, getOrgId } from '@devd-client/devd/auth';
import { RepoListResponse } from '@devd-client/devd/components';
import { useQuery } from '@tanstack/react-query';
import { JobRoleListResponse } from '../helpers/report.types';

const fetchRepos = (source: string): Promise<RepoListResponse> =>
  apiClient(
    `/v1/integration/${source}/org/${getOrgId()}/projects/lite?active=true`,
    {
      method: 'GET',
      headers: { authorization: `bearer ${getToken()}` },
    }
  );

export const useFetchRepoList = (source: string) =>
  useQuery(['report-repoList', source], () => fetchRepos(source));

export const getJobRoleList = (): Promise<JobRoleListResponse> =>
  apiClient(`/v1/org/${getOrgId()}/employee/jobRoles`, {
    method: 'GET',
    headers: {
      authorization: `bearer ${getToken()}`,
    },
  });

export const getContributorTableMetrics = (): Promise<any> =>
  apiClient(`/v1/contributorTable/metrics/${getOrgId()}`, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      authorization: `bearer ${getToken()}`,
    },
  });

export const useContributorTableMetrics = () =>
  useQuery(['report-contributorTableMetrics'], getContributorTableMetrics);

export interface ReportStatsDownloadFilter {
  startDate: string;
  endDate: string;
  teamIds: string[];
  sprintId: string;
}

const postReportDownload = (
  reportName: string,
  body: unknown
): Promise<{ dto?: string }> =>
  apiClient(`/v1/csv/reports/${reportName}/org/${getOrgId()}`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      authorization: `bearer ${getToken()}`,
    },
    body: JSON.stringify(body),
  });

const buildReportStatsFilter = (filter: ReportStatsDownloadFilter) => ({
  ...filter,
  orgId: getOrgId(),
  timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
});

/** Triggers async product stats xlsx export (emailed when ready). */
export const downloadProductStatsReport = (
  filter: ReportStatsDownloadFilter
): Promise<{ dto?: string }> =>
  postReportDownload('product_stats', {
    filter: buildReportStatsFilter(filter),
  });

/** Triggers async rework density xlsx export (emailed when ready). */
export const downloadReworkDensityReport = (
  filter: ReportStatsDownloadFilter & { projectsFilter?: string[] }
): Promise<{ dto?: string }> =>
  postReportDownload('rework_density', {
    filter: {
      ...buildReportStatsFilter(filter),
      projectsFilter: filter.projectsFilter ?? [],
    },
  });

/** Triggers async engineering stats xlsx export (emailed when ready). */
export const downloadEngineeringStatsReport = (
  filter: ReportStatsDownloadFilter,
  selectedColumns: string[]
): Promise<{ dto?: string }> =>
  postReportDownload('engineering_stats', {
    filter: {
      orgId: getOrgId(),
      startDate: filter.startDate,
      endDate: filter.endDate,
      sprintId: filter.sprintId,
      teamIds: filter.teamIds,
      projectsFilter: [],
      searchTerm: '',
      contributorFilters: {
        metricFilters: [],
        prog_lang: [],
        activeFilters: null,
        inActiveFilters: null,
        excluded: false,
      },
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
    selectedColumns,
  });
